import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClipboardCheck, User, ArrowLeft, LogIn, LogOut, XCircle, RotateCcw, Search, Download, Info, Heart, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UnbookedBanner } from "./UnbookedBanner";
import { WelcomePassRedeem } from "./WelcomePassRedeem";
import { LateAlerts } from "./LateAlerts";
import { LateFeeDialog, bdaTime } from "@/components/LateFeeDialog";

const today = () => new Date().toISOString().slice(0, 10);
const timeOf = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

const SAFETY = [["allergies", "Allergies"], ["dietary", "Dietary restrictions"], ["medical", "Medical info"], ["medications", "Medications"], ["special_support", "Special support"]];
const CONTACTS = [["parent_name", "Parent / guardian"], ["parent_phone", "Parent phone"], ["parent_phone_account", "Account phone"], ["parent_email", "Parent email"], ["secondary_guardian", "Secondary guardian"], ["emergency_contact", "Emergency contact"], ["emergency_phone", "Emergency phone"], ["authorized_pickups", "Authorized pickups"]];
const FUN = [["favorite_color", "Favourite colour"], ["favorite_animal", "Favourite animal"], ["favorite_food", "Favourite snack / food"], ["favorite_activity", "Favourite thing to do"], ["when_i_grow_up", "When I grow up…"], ["superpower", "My superpower would be…"]];

function ChildDetail({ child, onClose }) {
  if (!child) return null;
  const name = `${child.first_name || ""} ${child.last_name || ""}`.trim() || "Explorer";
  const Row = ([k, l]) => child[k] ? <div key={k} className="flex justify-between gap-3 py-1 border-b border-slate-100 last:border-0"><span className="text-xs text-slate-500 shrink-0">{l}</span><span className="text-sm font-medium text-navy text-right break-words">{child[k]}</span></div> : null;
  const funFilled = FUN.filter(([k]) => child[k]);
  return (
    <Dialog open={!!child} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="child-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-turquoise/15 flex items-center justify-center overflow-hidden shrink-0">
              {child.photo_url ? <img src={child.photo_url} className="w-full h-full object-cover" alt="" /> : <User className="w-7 h-7 text-turquoise" />}
            </div>
            <div className="text-left min-w-0">
              <p className="font-display font-extrabold text-navy truncate">{name}{child.preferred_name ? ` · "${child.preferred_name}"` : ""}</p>
              <p className="font-mono text-turquoise text-xs">{child.explorer_id}</p>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-2xl p-3" data-testid="child-basic">
            {[["school", "School"], ["grade", "Grade / Year"], ["date_of_birth", "Date of birth"], ["interests", "Preferred activities"]].map(Row)}
          </div>
          <div className="bg-coral/5 border border-coral/20 rounded-2xl p-3" data-testid="child-safety">
            <p className="text-xs font-bold text-coral uppercase tracking-wider mb-1">⚠ Safety & Care</p>
            {SAFETY.some(([k]) => child[k]) ? SAFETY.map(Row) : <p className="text-sm text-slate-400">No allergies or medical notes on file.</p>}
          </div>
          <div className="bg-slate-50 rounded-2xl p-3" data-testid="child-contacts">
            <p className="text-xs font-bold text-navy uppercase tracking-wider mb-1">Contacts & Pickup</p>
            {CONTACTS.map(Row)}
          </div>
          <div className="bg-turquoise/5 border border-turquoise/20 rounded-2xl p-3" data-testid="child-funfacts">
            <p className="text-xs font-bold text-turquoise uppercase tracking-wider mb-1 flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> Get to know me</p>
            {funFilled.length ? funFilled.map(Row) : <p className="text-sm text-slate-400">The family hasn't added fun facts yet.</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function StaffAttendance() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState(today());
  const [roster, setRoster] = useState([]);
  const [summary, setSummary] = useState({ expected: 0, present: 0, in_building: 0, checked_out: 0, absent: 0, unmarked: 0 });
  const [lateFor, setLateFor] = useState(null);
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const openDetail = async (childId) => {
    try { const { data } = await api.get(`/staff/children/${childId}/profile`); setDetail(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const load = useCallback(() => {
    api.get(`/staff/roster?date=${date}`).then((r) => { setRoster(r.data.roster); setSummary(r.data.summary); }).catch((e) => toast.error(apiErr(e)));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const mark = async (id, status) => {
    try {
      const { data } = await api.post("/staff/attendance", { booking_id: id, status });
      if (data.credit_issued) toast.success("Marked absent · Adventure Credit added for the family");
      load();
    }
    catch (e) { toast.error(apiErr(e)); }
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [["Explorer", "Explorer ID", "School", "Grade", "Package", "Attendance", "Checked In", "Checked Out", "Transport", "Parent Phone", "Allergies"]];
    roster.forEach((r) => rows.push([
      r.preferred_name || r.child_name, r.explorer_id, r.school, r.grade, r.package_type,
      r.attendance || "not arrived", timeOf(r.checkin_at), timeOf(r.checkout_at), r.transport, r.parent_phone, r.allergies,
    ]));
    const csv = rows.map((row) => row.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `attendance-${date}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return roster;
    return roster.filter((r) => r.child_name.toLowerCase().includes(t) || (r.explorer_id || "").toLowerCase().includes(t) || (r.school || "").toLowerCase().includes(t));
  }, [roster, q]);

  const Stat = ({ label, value, color }) => (
    <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-center" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <p className={`font-display font-black text-2xl ${color}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-white/50">{label}</p>
    </div>
  );

  const badge = (r) => {
    if (r.attendance === "present") return <span className="text-[10px] font-bold text-turquoise bg-turquoise/15 rounded-full px-2 py-0.5">In building</span>;
    if (r.attendance === "checked_out") return <span className="text-[10px] font-bold text-white/60 bg-white/10 rounded-full px-2 py-0.5">Checked out</span>;
    if (r.attendance === "absent") return <span className="text-[10px] font-bold text-coral bg-coral/15 rounded-full px-2 py-0.5">Absent</span>;
    return <span className="text-[10px] font-bold text-gold bg-gold/15 rounded-full px-2 py-0.5">Not arrived</span>;
  };

  return (
    <div className="min-h-screen bg-navy text-white">
      <header className="h-16 flex items-center justify-between gap-2 px-4 border-b border-white/10">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm hidden sm:block">ATTENDANCE REGISTER</p></div>
        <div className="flex items-center gap-1 overflow-x-auto [&>button]:shrink-0">
          <Button variant="ghost" size="sm" onClick={() => nav("/staff")} className="text-white/70">Scanner</Button>
          <Button variant="ghost" size="sm" onClick={() => nav("/staff/transport")} className="text-white/70">Transport</Button>
          <Button variant="ghost" size="sm" onClick={() => nav(["admin", "manager"].includes(user?.role) ? "/admin" : "/dashboard")} className="text-white/70"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }}>✕</Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-5">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardCheck className="w-6 h-6 text-turquoise" />
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl bg-white/10 border-white/20 text-white w-44" data-testid="attendance-date" />
          <Button onClick={exportCsv} disabled={roster.length === 0} className="ml-auto bg-gold/20 hover:bg-gold text-gold hover:text-navy font-bold rounded-full" data-testid="attendance-export"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-5" data-testid="attendance-summary">
          <Stat label="Expected" value={summary.expected} color="text-white" />
          <Stat label="In Building" value={summary.in_building} color="text-turquoise" />
          <Stat label="Present" value={summary.present} color="text-gold" />
          <Stat label="Checked Out" value={summary.checked_out} color="text-white/70" />
          <Stat label="Absent" value={summary.absent} color="text-coral" />
        </div>

        <LateAlerts />
        <WelcomePassRedeem />
        <UnbookedBanner date={date} refreshKey={roster.length} />

        <div className="relative mb-4">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ID or school" className="rounded-xl bg-white/10 border-white/20 text-white pl-9" data-testid="attendance-search" />
        </div>

        {filtered.length === 0 && <p className="text-white/40 text-sm">No explorers booked for this day.</p>}
        <div className="space-y-2">
          {filtered.map((r) => (
            <div key={r.id} className="bg-white/5 border border-white/10 rounded-2xl p-3 flex items-center gap-3" data-testid={`roster-row-${r.id}`}>
              <div className="w-12 h-12 rounded-xl bg-turquoise/20 flex items-center justify-center overflow-hidden shrink-0">
                {r.photo_url ? <img src={r.photo_url} className="w-full h-full object-cover" alt="" /> : <User className="w-6 h-6 text-turquoise" />}
              </div>
              <button onClick={() => openDetail(r.child_id)} className="flex-1 min-w-0 text-left" data-testid={`child-detail-btn-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display font-bold text-white truncate">{r.preferred_name || r.child_name}</p>
                  {badge(r)}
                  {r.payment_status !== "confirmed" && <span className="text-[10px] font-bold text-red-200 bg-red-500/25 rounded-full px-2 py-0.5" data-testid={`unpaid-${r.id}`}>UNPAID · pay before arrival</span>}
                  <Info className="w-3.5 h-3.5 text-white/40" />
                </div>
                <p className="font-mono text-turquoise text-[11px]">{r.explorer_id} · {r.school}</p>
                {r.allergies && <p className="text-[11px] text-coral">⚠ {r.allergies}</p>}
                {(r.checkin_at || r.checkout_at) && (
                  <p className="text-[10px] text-white/50">{r.checkin_at ? `In ${timeOf(r.checkin_at)}` : ""}{r.checkout_at ? ` · Out ${timeOf(r.checkout_at)}` : ""}</p>
                )}
              </button>
              <div className="flex flex-col gap-1.5 shrink-0">
                {r.attendance !== "present" && r.attendance !== "checked_out" && (
                  <button onClick={() => mark(r.id, "checked_in")} data-testid={`checkin-${r.id}`} className="text-[11px] font-semibold bg-turquoise/20 hover:bg-turquoise text-turquoise hover:text-navy rounded-full px-3 py-1 transition-colors flex items-center gap-1"><LogIn className="w-3 h-3" /> Check in</button>
                )}
                {r.attendance === "present" && (
                  <button onClick={() => mark(r.id, "checked_out")} data-testid={`checkout-${r.id}`} className="text-[11px] font-semibold bg-gold/20 hover:bg-gold text-gold hover:text-navy rounded-full px-3 py-1 transition-colors flex items-center gap-1"><LogOut className="w-3 h-3" /> Check out</button>
                )}
                {!r.attendance && (
                  <button onClick={() => mark(r.id, "absent")} data-testid={`absent-${r.id}`} className="text-[11px] font-semibold bg-coral/15 hover:bg-coral text-coral hover:text-white rounded-full px-3 py-1 transition-colors flex items-center gap-1"><XCircle className="w-3 h-3" /> Absent</button>
                )}
                {r.attendance && (
                  <button onClick={() => setLateFor({ id: r.child_id, name: r.preferred_name || r.child_name, time: r.checkout_at ? bdaTime(new Date(r.checkout_at)) : bdaTime() })} data-testid={`late-fee-${r.id}`} className="text-[11px] font-semibold bg-coral/15 hover:bg-coral text-coral hover:text-white rounded-full px-3 py-1 transition-colors flex items-center gap-1"><Clock className="w-3 h-3" /> Late fee</button>
                )}
                {r.attendance && (
                  <button onClick={() => mark(r.id, "reset")} data-testid={`reset-${r.id}`} className="text-[11px] font-semibold text-white/50 hover:text-white rounded-full px-3 py-1 transition-colors flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Reset</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
      <ChildDetail child={detail} onClose={() => setDetail(null)} />
      <LateFeeDialog child={lateFor} defaultTime={lateFor?.time} onClose={() => setLateFor(null)} />
    </div>
  );
}
